import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type CheckBoxCheckedStateConstantType,
    type CheckBoxStateStyleConstantType,
    type CheckBoxMeasuresConstantType,
    type CheckBoxColorsConstantType,
    type CheckBoxTypeConstantType,
    type CheckBoxDynamicStyleType,
    type CheckBoxLegacyChecked,
    type CheckBoxCheckedState,
    type CheckBoxStateStyles,
    type CheckBoxMeasures,
    type CheckBoxColors,
    type CheckBoxTypes
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import type {
    SelectionControlCheckedState
} from "../selectionControl/type";
import {
    SELECTION_CONTROL_ELEVATION_LEVEL
} from "../selectionControl/stylesheet";
import {
    getFocusRingStyle,
    getStateColorKey
} from "../../helpers/theme/state";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getMotionTransition
} from "../../helpers/motion";
import {
    webStyle
} from "../../utils";

export const CHECK_BOX_LEGACY_CHECKED_STATES: Record<CheckBoxLegacyChecked, CheckBoxCheckedState> = {
    partially: "indeterminate",
    checked: "checked"
};

export const CHECK_BOX_ARIA_CHECKED: Record<CheckBoxCheckedState, SelectionControlCheckedState> = {
    indeterminate: "mixed",
    unchecked: false,
    checked: true
};

export const getCheckBoxCheckedState = ({
    isIndeterminate,
    isChecked
}: CheckBoxCheckedStateConstantType): CheckBoxCheckedState => {
    if(isIndeterminate) {
        return "indeterminate";
    }

    if(typeof isChecked === "string") {
        return CHECK_BOX_LEGACY_CHECKED_STATES[isChecked];
    }

    return isChecked ? "checked" : "unchecked";
};

export const getCheckBoxMeasures = ({
    iconSizes,
    radiuses,
    borders,
    spaces
}: CheckBoxMeasuresConstantType): CheckBoxMeasures => {
    return {
        glyphSize: iconSizes.small - spaces.spacingXxs * 2,
        borderRadius: radiuses.form / 2,
        borderWidth: borders.selection,
        boxSize: iconSizes.small
    };
};

export const getCheckBoxType = ({
    colors,
    type
}: CheckBoxTypeConstantType): CheckBoxTypes => {
    const filledColors = getTypeColors({
        variant: "filled",
        colors,
        type
    });

    return {
        selectedOverlayColor: filledColors.overlayColor,
        selectedColor: filledColors.containerColor,
        glyphColor: filledColors.iconColor,
        overlayColor: getStateColorKey({
            type
        })
    };
};

export const getCheckBoxColors = ({
    currentType,
    isDisabled,
    isSelected,
    isError,
    colors
}: CheckBoxColorsConstantType): CheckBoxColors => {
    if(isDisabled) {
        const disabledFilledColors = getTypeColors({
            variant: "filled",
            type: "neutral",
            isDisabled,
            colors
        });

        const disabledOutlineColors = getTypeColors({
            variant: "outline",
            type: "neutral",
            isDisabled,
            colors
        });

        return {
            hoverBorderColor: disabledOutlineColors.borderColor,
            containerColor: disabledFilledColors.containerColor,
            overlayColor: disabledFilledColors.overlayColor,
            borderColor: disabledOutlineColors.borderColor,
            spinnerColor: disabledFilledColors.iconColor,
            glyphColor: disabledFilledColors.iconColor
        };
    }

    if(isSelected) {
        return {
            overlayColor: currentType.selectedOverlayColor,
            hoverBorderColor: currentType.selectedColor,
            containerColor: currentType.selectedColor,
            spinnerColor: currentType.selectedColor,
            borderColor: currentType.selectedColor,
            glyphColor: currentType.glyphColor
        };
    }

    const restColors = getTypeColors({
        variant: "outline",
        type: "neutral",
        colors
    });

    const borderColor = isError ? colors.content.border.danger : colors.content.icon.low;

    return {
        hoverBorderColor: isError ? borderColor : colors.content.icon.mid,
        containerColor: restColors.containerColor,
        spinnerColor: currentType.selectedColor,
        overlayColor: currentType.overlayColor,
        glyphColor: currentType.glyphColor,
        borderColor
    };
};

export const getCheckBoxStateStyle = ({
    isReduceMotionEnabled,
    currentMeasures,
    isFocusVisible,
    currentColors,
    shapeConfigs,
    durations,
    isHovered,
    isPressed,
    borders,
    colors,
    type
}: CheckBoxStateStyleConstantType): CheckBoxStateStyles => {
    const focusRingStyle = getFocusRingStyle({
        colorKey: getStateColorKey({
            type
        }),
        isFocusVisible,
        borders,
        colors
    });

    const styles = {
        loading: {
            ...focusRingStyle
        } as Mutable<ViewStyle>,
        box: {
            ...focusRingStyle
        } as Mutable<ViewStyle>
    };

    if(isFocusVisible) {
        Object.assign(styles.loading, getCornerStyle({
            radius: currentMeasures.borderRadius,
            shapeConfigs
        }));
    }

    if(isHovered || isPressed) {
        styles.box.borderColor = currentColors.hoverBorderColor;
    }

    Object.assign(styles.box, webStyle(getMotionTransition({
        properties: [
            "background-color",
            "border-color"
        ],
        isReduceMotionEnabled,
        intent: "hover",
        durations
    })));

    return styles;
};

const stylesheet = StyleSheet.create({
    box: {
        justifyContent: "center",
        borderStyle: "solid",
        alignItems: "center"
    },
    glyphContainer: {
        justifyContent: "center",
        alignItems: "center"
    }
});

export const useStyles = ({
    currentMeasures,
    currentColors,
    shapeConfigs,
    shadowAlpha,
    elevations,
    isDisabled,
    colors
}: CheckBoxDynamicStyleType) => {
    const styles = {
        box: {
            backgroundColor: currentColors.containerColor,
            ...getCornerStyle({
                radius: currentMeasures.borderRadius,
                shapeConfigs
            }),
            borderWidth: currentMeasures.borderWidth,
            borderColor: currentColors.borderColor,
            height: currentMeasures.boxSize,
            width: currentMeasures.boxSize
        } as Mutable<ViewStyle>,
        glyphContainer: {
            height: currentMeasures.glyphSize,
            width: currentMeasures.glyphSize
        } as Mutable<ViewStyle>
    };

    if(!isDisabled) {
        Object.assign(styles.box, getElevationStyle({
            level: SELECTION_CONTROL_ELEVATION_LEVEL,
            shadowColor: colors.system.shadow,
            shadowAlpha,
            elevations
        }));
    }

    return styles;
};
export default stylesheet;
